import type { ReactNode } from "react";
import { useState } from "react";
import { t } from "@/i18n-js/instance";
import { useButtonVariant } from "@circle-react/helpers/useButtonVariant";
import { Button } from "@circle-react-uikit/Button";
import { Fieldset } from "@circle-react-uikit/Fieldset";
import { Form } from "@circle-react-uikit/Form";
import { useFilterContext } from "../../Filter";
import { SearchBar } from "./SearchBar";

type ValueType = string | number;

interface CheckboxFilterOption {
  label: string;
  richLabel?: ReactNode;
  value: ValueType;
}

interface SearchProps {
  label?: string;
  placeholder?: string;
  searchPlaceholder?: string;
}

interface CheckboxFilterProps {
  activeText?: string | (string | null)[] | null;
  hasSearch?: boolean;
  legend: string;
  name: string;
  options: CheckboxFilterOption[];
  searchProps?: SearchProps;
}

export const CheckboxFilter = ({
  options,
  name,
  legend,
  hasSearch,
  searchProps,
  activeText,
}: CheckboxFilterProps) => {
  const { onApply } = useFilterContext();
  const [searchTerm, setSearchTerm] = useState("");
  const primaryButtonVariant = useButtonVariant();

  const filteredOptions = options.filter(option =>
    !searchTerm
      ? option
      : option.label.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const getSelectedItems = (): ValueType[] => {
    if (Array.isArray(activeText)) {
      return activeText
        .filter((item: string | null): item is string => item !== null)
        .map(item => +item);
    }
    return activeText?.split(",") ?? [];
  };

  const getDefaultValues = () => {
    const selectedItems = getSelectedItems();
    const defaults = options.reduce(
      (acc: Record<ValueType, boolean>, option) => {
        acc[option.value] = selectedItems.includes(option.value);
        return acc;
      },
      {},
    );
    return {
      [name]: defaults,
    };
  };

  const handleApply = (
    submitData: { [x: string]: Record<ValueType, boolean> },
    { reset }: { reset: () => void },
  ): void => {
    // Find items with value true
    const checkedValues = Object.entries(submitData[name]).filter(
      ([, value]) => value,
    );
    const checkedKeys = checkedValues.map(([key]) => key).join(",");
    onApply(checkedKeys);
    reset();
  };

  return (
    <div className="flex flex-col gap-y-5 p-4">
      {hasSearch && (
        <SearchBar
          name={name}
          value={searchTerm}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setSearchTerm(e.target.value)
          }
          searchProps={searchProps}
        />
      )}
      <Form defaultValues={getDefaultValues()} onSubmit={handleApply}>
        <div className="flex flex-col gap-y-3">
          <div className="max-h-[20rem] gap-y-4 overflow-y-auto p-1">
            <Fieldset legend={legend} verticalSpacing="tight">
              {filteredOptions.map(option => (
                <Form.Item
                  key={option.value}
                  name={`${name}.${option.value}`}
                  id={`${name}-${option.value}`}
                  hideLabel
                  hideBorder
                  hideDescription
                  isFirstChild
                  className="!pb-0"
                >
                  <Form.Checkbox
                    name={`${name}.${option.value}`}
                    option={option}
                    labelClassName="text-base font-normal"
                    className="!mt-0"
                  />
                </Form.Item>
              ))}
            </Fieldset>
          </div>
          <Button variant={primaryButtonVariant} type="submit" full>
            {t("filters.apply")}
          </Button>
        </div>
      </Form>
    </div>
  );
};
